import { GD } from "src/GD";
import { mergeObject } from "src/utils/tools";
import { createName, FormParser } from "src/view/FormParser";
import { useEffect, useState } from "react";
import { HBox, VBox } from "@ui/box/Box";
import { Typo } from "@ui/text/Typo";
import Spacer from "@ui/spacer/Spacer";
import { client_registration_form_step_1_decorator } from "../../registerPage/steps/step_1";
import { Rows } from "@ui/table/Rows";
import { client_registration_form_step_5_decorator } from "../../registerPage/steps/step_5";
import { client_registration_form_step_6_decorator } from "../../registerPage/steps/step_6";
import { IconBusiness } from "src/view/conponents/icons/IconBusiness";
import { IconPeople } from "src/view/conponents/icons/IconPeople";
import { IconPersonMoney } from "src/view/conponents/icons/IconPersonMoney";
import { IconAccountBalance } from "src/view/conponents/icons/IconAccountBalance";
import { IconFolder } from "src/view/conponents/icons/IconFolder";

const sectionIcons: Record<string, React.FC<{ color?: string; size?: number }>> = {
	general: IconBusiness,
	directors: IconPeople,
	beneficial_owners: IconPersonMoney,
	payout_account_details: IconAccountBalance,
	documents: IconFolder,
};

export interface IClientInfoFormProps {
	clientID: number;
}

export const ClientInfoForm = ({ clientID }: IClientInfoFormProps) => {
	const sections = ["general", "directors", "beneficial_owners", "payout_account_details", "documents"] as const;
	const [section, setSection] = useState<(typeof sections)[number]>(sections[0]);

	let content = null;
	switch (section) {
		case "general":
			content = (
				<FormParser
					previewMode={true}
					key="general"
					onSubmit={async (_data: any) => {}}
					onRequest={async () => {
						//setBusyLoad(true);

						const response = await GD.REQ_API.request({
							method: "clients/form/get",
							data: {
								target_id: clientID,
								target_type: "client",
								type: "client_info",
								subobject: "general_company_information",
							},
						});

						if (response.error) {
							//setBusyLoad(false);
							return response;
						}
						if (response && response.data && response.data.data) {
							response.data.data = { model: JSON.parse(JSON.stringify(response.data.data)) };
							mergeObject(response.data.data, client_registration_form_step_1_decorator);
						}
						//setBusyLoad(false);
						return response;
					}}
				/>
			);
			break;
		case "directors":
			content = <ClientDirectors clientID={clientID} />;
			break;
		case "beneficial_owners":
			content = <ClientBeneficiaries clientID={clientID} />;
			break;
		case "payout_account_details":
			content = (
				<FormParser
					previewMode={true}
					key="payout_account_details"
					onSubmit={async (_data: any) => {}}
					onRequest={async () => {
						//setBusyLoad(true);
						const response = await GD.REQ_API.request({
							method: "clients/form/get",
							data: {
								target_id: clientID,
								target_type: "client",
								type: "client_info",
								subobject: "payout_account_details",
							},
						});
						if (response.error) {
							//setBusyLoad(false);
							return response;
						}
						if (response && response.data && response.data.data) {
							response.data.data = { model: JSON.parse(JSON.stringify(response.data.data)) };
							mergeObject(response.data.data, client_registration_form_step_5_decorator);
						}
						//setBusyLoad(false);
						return response;
					}}
				/>
			);
			break;
		case "documents":
			content = (
				<FormParser
					previewMode={true}
					key="documents"
					onSubmit={async (_data: any) => {}}
					onRequest={async () => {
						//setBusyLoad(true);

						const response = await GD.REQ_API.request({
							method: "clients/form/get",
							data: {
								target_id: clientID,
								target_type: "client",
								type: "client_info",
								subobject: "document_uploads",
							},
						});
						if (response.error) {
							//setBusyLoad(false);
							return response;
						}
						if (response && response.data && response.data.data) {
							response.data.data = { model: JSON.parse(JSON.stringify(response.data.data)) };
							mergeObject(response.data.data, client_registration_form_step_6_decorator);
						}
						//setBusyLoad(false);
						return response;
					}}
				/>
			);
			break;
	}

	return (
		<VBox
			sx={{
				position: "relative",
				flexGrow: 1,
			}}>
			<HBox
				sx={{
					flexGrow: 1,
				}}>
				<VBox
					sx={{
						overflowY: "auto",
						minWidth: "185px",
					}}>
					{sections.map(sec => {
						return (
							<HBox
								key={sec}
								sx={{
									borderLeft: "5px solid " + (sec === section ? "rgb(0, 145, 255)" : "transparent"),
									borderBottom: "1px solid rgba(0,0,0,0.1)",
									cursor: "pointer",
									padding: "20px",
									//borderRadius: "8px",
									gap: "10px",
									alignItems: "center",
									whiteSpace: "nowrap",
									overflow: "hidden",
								}}
								onClick={() => {
									setSection(sec);
								}}>
								{(() => {
									const Icon = sectionIcons[sec];
									return Icon ? <Icon color={sec === section ? "rgb(0, 145, 255)" : "#666"} size={20} /> : null;
								})()}

								<Typo
									bold={sec === section}
									variant="medium"
									sx={{
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
										textTransform: "capitalize",
									}}>
									{createName(sec)}
								</Typo>
							</HBox>
						);
					})}
				</VBox>
				<Spacer size="20px" />

				<VBox
					sx={{
						flexGrow: 1,
						backgroundColor: "#ebedf461",
						padding: "20px",
					}}>
					{content}
				</VBox>
			</HBox>
		</VBox>
	);
};

const ClientDirectors = ({ clientID }: { clientID: number }) => {
	const [directors, setDirectors] = useState<IClientDirector[]>([]);

	const fetchDirectors = async () => {
		//!TODO: loading and error states;
		//setError(null);
		//setBusyLoad(true);

		const response = await GD.REQ_API.request({
			method: "clients/person/list",
			data: {
				client_id: clientID,
				type: "director",
			},
		});

		if (response.error) {
			//setBusyLoad(false);
			//setError(response.error);
			return;
		}

		if (response && response.data && Array.isArray(response.data)) {
			setDirectors(response.data.filter((w: any) => w.status !== "deleted" && w.id > 0 && w.type === "director"));
		}

		//setBusyLoad(false);
	};

	useEffect(() => {
		fetchDirectors();
	}, [clientID]);

	return <Rows<IClientDirector> data={directors || []} order={["__itemType", "name", "dob", "status"]} itemType="director" />;
};

const ClientBeneficiaries = ({ clientID }: { clientID: number }) => {
	const [beneficiaries, setBeneficiaries] = useState<IClientBeneficiary[]>([]);

	const fetchBeneficiaries = async () => {
		//!TODO: loading and error states;
		//setError(null);
		//setBusyLoad(true);

		const response = await GD.REQ_API.request({
			method: "clients/person/list",
			data: {
				client_id: clientID,
				type: "beneficial_owner",
			},
		});

		if (response.error) {
			//setBusyLoad(false);
			//setError(response.error);
			return;
		}

		if (response && response.data && Array.isArray(response.data)) {
			setBeneficiaries(response.data.filter((w: any) => w.status !== "deleted" && w.id > 0 && w.type === "beneficial_owner"));
		}

		//setBusyLoad(false);
	};

	useEffect(() => {
		fetchBeneficiaries();
	}, [clientID]);

	return <Rows<IClientBeneficiary> data={beneficiaries || []} order={["__itemType", "name", "dob", "status"]} itemType="beneficiary" />;
};
